import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, AlertTriangle, ChevronRight, X, Sparkles } from "lucide-react";
import { fetchConcierge } from "../lib/api";
import { getCategory } from "../lib/categories";
import { tr } from "../lib/i18n";

export const ConciergeBanner = ({ onOpenDoc }) => {
  const [dismissed, setDismissed] = useState([]);
  const { data } = useQuery({ queryKey: ["concierge"], queryFn: fetchConcierge });

  const alerts = (data?.alerts || []).filter((a) => !dismissed.includes(a.id));
  if (alerts.length === 0) return null;

  const urgency = (days) =>
    days < 0
      ? "border-rose-300 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/30"
      : days <= 14
      ? "border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30"
      : "border-sky-300 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/30";

  return (
    <div data-testid="concierge-banner" className="mb-6 space-y-2.5">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-medium">{tr("Concierge — things worth doing soon")}</h3>
      </div>
      <AnimatePresence>
        {alerts.map((a) => {
          const cat = getCategory(a.category);
          return (
            <motion.div
              key={a.id}
              data-testid="concierge-alert"
              layout
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, height: 0, marginBottom: 0 }}
              className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${urgency(a.days_until)}`}
            >
              <div className="h-9 w-9 rounded-lg bg-white/70 dark:bg-black/20 grid place-items-center shrink-0">
                {a.days_until < 0 ? (
                  <AlertTriangle className="h-4 w-4 text-rose-600" />
                ) : (
                  <Bell className="h-4 w-4" style={{ color: cat.iconColor }} />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium leading-snug">{a.suggestion}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {a.label} · {a.date}
                </p>
              </div>
              {!String(a.document_id || "").startsWith("followup:") && <button
                data-testid="concierge-view"
                onClick={() => onOpenDoc?.(a.document_id)}
                className="text-xs font-medium text-primary inline-flex items-center gap-0.5 shrink-0 hover:underline"
              >
                View <ChevronRight className="h-3.5 w-3.5" />
              </button>}
              <button
                data-testid="concierge-dismiss"
                onClick={() => setDismissed((d) => [...d, a.id])}
                className="text-muted-foreground hover:text-foreground shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};
